import React, { useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { closeModal } from 'reducers/modals';
// components
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import Modal from 'components/Modal';
import Button from 'components/Button';
import Input from 'components/Input';
import Preloader from 'components/Preloader';
import SingleDatePicker from 'components/DatePicker/SingleDatePicker/SingleDatePicker';
// endpoints
import { getBinCycles, updateBinCycle, deleteBinCycle } from 'endpoints/feedDeliveries';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatNumber } from 'utils';
import { formatDateForUser } from 'utils/regionalDateHelper';
import { lbsToDisplay, displayToLbs, weightUnit } from '../utils';
// selectors
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';

const BinHistoryModal = ({ farmId, bin, measurementSystem, countryCode, onChanged, closeModal }) => {
  const [cycles, setCycles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editedId, setEditedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [draft, setDraft] = useState({});
  const unit = weightUnit(measurementSystem);
  const formatDate = (date) => (date ? formatDateForUser(date, countryCode) : null);

  const loadCycles = () => {
    setIsLoading(true);
    getBinCycles(farmId, bin.id)
      .then(setCycles)
      .catch(toastResponseErrors)
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadCycles();
  }, []);

  const startEdit = (cycle) => {
    setDeletingId(null);
    setEditedId(cycle.id);
    setDraft({
      opened_on: moment(cycle.opened_on),
      closed_on: cycle.closed_on ? moment(cycle.closed_on) : null,
      remaining_at_close: cycle.remaining_at_close === null
        ? ''
        : lbsToDisplay(cycle.remaining_at_close, measurementSystem),
    });
  };

  // Clearing a date input makes the picker emit null - the draft is not savable then
  const isDraftValid = (cycle) => Boolean(draft.opened_on) && (!cycle.closed_on || Boolean(draft.closed_on));

  // Fixing a wrong date here is what resets the bin's current state on the main list
  const saveEdit = async (cycle) => {
    if (!isDraftValid(cycle)) return;

    try {
      const resource = { opened_on: draft.opened_on.format('YYYY-MM-DD') };
      if (cycle.closed_on) {
        resource.closed_on = draft.closed_on.format('YYYY-MM-DD');
        resource.remaining_at_close = displayToLbs(draft.remaining_at_close || 0, measurementSystem);
      }
      await updateBinCycle(farmId, bin.id, cycle.id, resource);
      setEditedId(null);
      loadCycles();
      onChanged();
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const confirmDelete = async (cycle) => {
    try {
      await deleteBinCycle(farmId, bin.id, cycle.id);
      setDeletingId(null);
      loadCycles();
      onChanged();
    } catch (error) {
      toastResponseErrors(error);
    }
  };

  const renderReadRow = (cycle) => (
    <div className="cycle-info">
      <div className="cycle-dates">
        <strong>{formatDate(cycle.opened_on)}</strong>
        {' — '}
        <strong>
          {cycle.closed_on
            ? formatDate(cycle.closed_on)
            : <FormattedMessage id="component.feedDeliveries.stillOpen" />}
        </strong>
        <span className="ml-10">{cycle.pig_group_name}</span>
      </div>
      {cycle.closed_on && (
        <div className="cycle-stats">
          <FormattedMessage
            id="component.feedDeliveries.cycleStats"
            values={{
              consumed: `${formatNumber(lbsToDisplay(cycle.consumed, measurementSystem))} ${unit}`,
              remaining: `${formatNumber(lbsToDisplay(cycle.remaining_at_close, measurementSystem))} ${unit}`,
            }}
          />
          {(cycle.withdrawal_date_us || cycle.withdrawal_date_export) && (
            <div className="cycle-withdrawal">
              <FormattedMessage
                id="component.feedDeliveries.cycleWithdrawal"
                values={{
                  us: cycle.withdrawal_date_us ? formatDate(cycle.withdrawal_date_us) : '—',
                  targetExport: cycle.withdrawal_date_export ? formatDate(cycle.withdrawal_date_export) : '—',
                }}
              />
            </div>
          )}
        </div>
      )}
      <div className="cycle-actions">
        <Button small onClick={() => startEdit(cycle)}>
          <FormattedMessage id="general.button.edit" />
        </Button>
        {deletingId === cycle.id ? (
          <span className="delete-confirm">
            <FormattedMessage id="component.feedDeliveries.deleteCycleConfirm" />
            <Button small red onClick={() => confirmDelete(cycle)}>
              <FormattedMessage id="general.button.yes" />
            </Button>
            <Button small onClick={() => setDeletingId(null)}>
              <FormattedMessage id="general.button.no" />
            </Button>
          </span>
        ) : (
          <Button small onClick={() => { setEditedId(null); setDeletingId(cycle.id); }}>
            <FormattedMessage id="general.button.delete" />
          </Button>
        )}
      </div>
    </div>
  );

  const renderEditRow = (cycle) => (
    <div className="cycle-edit">
      <div className="form-row">
        <span className="input-label"><FormattedMessage id="component.feedDeliveries.binOpenDate" /></span>
        <SingleDatePicker
          inputId={`cycleOpenDate-${cycle.id}`}
          className="date-input"
          date={draft.opened_on}
          onDateChange={(date) => setDraft((prev) => ({ ...prev, opened_on: date }))}
          isOutsideRange={(day) => day.isAfter(moment(), 'day')}
        />
      </div>
      {cycle.closed_on && (
        <>
          <div className="form-row">
            <span className="input-label"><FormattedMessage id="component.feedDeliveries.binCloseDate" /></span>
            <SingleDatePicker
              inputId={`cycleCloseDate-${cycle.id}`}
              className="date-input"
              date={draft.closed_on}
              onDateChange={(date) => setDraft((prev) => ({ ...prev, closed_on: date }))}
              isOutsideRange={(day) => day.isAfter(moment(), 'day')}
            />
          </div>
          <div className="form-row">
            <span className="input-label">
              <FormattedMessage id="component.feedDeliveries.remainingAtClose" /> ({unit})
            </span>
            <Input
              type="number"
              value={draft.remaining_at_close}
              onChange={(e) => setDraft((prev) => ({ ...prev, remaining_at_close: e.target.value }))}
              min={0}
              step={0.01}
            />
          </div>
        </>
      )}
      <div className="cycle-actions">
        <Button small primary disabled={!isDraftValid(cycle)} onClick={() => saveEdit(cycle)}>
          <FormattedMessage id="general.button.save" />
        </Button>
        <Button small onClick={() => setEditedId(null)}>
          <FormattedMessage id="general.button.cancel" />
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title={<FormattedMessage id="component.feedDeliveries.binHistoryTitle" values={{ binName: bin.name }} />}
      className="BinHistoryModal"
    >
      <section className="modal-body">
        <Preloader isActive={isLoading} />
        {!isLoading && !cycles.length && (
          <FormattedMessage id="component.feedDeliveries.noCycles" />
        )}
        {cycles.map((cycle) => (
          <div className="cycle-row" key={cycle.id}>
            {editedId === cycle.id ? renderEditRow(cycle) : renderReadRow(cycle)}
          </div>
        ))}
      </section>

      <section className="modal-footer text-right">
        <Button onClick={closeModal}>
          <FormattedMessage id="general.close" />
        </Button>
      </section>
    </Modal>
  );
};

BinHistoryModal.propTypes = {
  farmId: T.number.isRequired,
  bin: T.object.isRequired,
  measurementSystem: T.oneOf(['imperial', 'metric']),
  countryCode: T.string,
  onChanged: T.func.isRequired,
  closeModal: T.func.isRequired,
};

export default connect(
  (state) => ({
    countryCode: getCurrentCountryCode(state),
  }), { closeModal }
)(BinHistoryModal);
